/**
 * Shared account bootstrap — one GET /api/subscriptions/status for
 * credits + summary + plan/storage entitlements. Concurrent callers share
 * the in-flight request; short TTL avoids reload stampedes.
 */
import { getStatus as getSubscriptionStatus } from "./subscriptions";

const CACHE_TTL_MS = 4000;

let cacheToken = null;
let cachePayload = null;
let cacheAt = 0;
let inflightToken = null;
let inflightPromise = null;

function normalizeBootstrap(status) {
  if (!status || status.success === false) {
    return null;
  }
  const benefits = status.benefits || {};
  const credits =
    status.credits ??
    benefits.remaining_credits ??
    benefits.available_credits ??
    benefits.credit_display_total ??
    0;
  return {
    success: true,
    credits: Number(credits) || 0,
    lifetime_credits: Number(status.lifetime_credits) || 0,
    monthly_credits: Number(status.monthly_credits) || 0,
    subscription: status.subscription ?? null,
    benefits,
    raw: status,
  };
}

/**
 * @param {string} token
 * @param {{ force?: boolean }} [options]
 */
export async function fetchAccountBootstrap(token, options = {}) {
  const force = Boolean(options.force);
  const now = Date.now();

  if (
    !force &&
    cacheToken === token &&
    cachePayload &&
    now - cacheAt < CACHE_TTL_MS
  ) {
    return cachePayload;
  }

  if (!force && inflightToken === token && inflightPromise) {
    return inflightPromise;
  }

  inflightToken = token;
  inflightPromise = getSubscriptionStatus(token)
    .then((status) => {
      const normalized = normalizeBootstrap(status);
      if (normalized) {
        cacheToken = token;
        cachePayload = normalized;
        cacheAt = Date.now();
      }
      return normalized;
    })
    .finally(() => {
      if (inflightToken === token) {
        inflightToken = null;
        inflightPromise = null;
      }
    });

  return inflightPromise;
}

export function invalidateAccountBootstrap() {
  cacheToken = null;
  cachePayload = null;
  cacheAt = 0;
}

export default {
  fetchAccountBootstrap,
  invalidateAccountBootstrap,
};
